<script setup>
import { computed } from "vue";
import { DAILY_TOKEN_GOAL, TOTAL_TOKEN_MILESTONES, YEARLY_TOKEN_GOAL } from "@/shared/config/constants";
import { compact } from "@/shared/utils/format";

const props = defineProps({
  mode: { type: String, default: "day" },
  totalTokens: { type: Number, default: 0 },
  isToday: { type: Boolean, default: true },
  date: { type: String, required: true },
  yearTotal: { type: Number, default: null },
});

const isTotalMode = computed(() => props.mode === "total");
const isYearMode = computed(() => props.mode === "year");
const finalMilestone = TOTAL_TOKEN_MILESTONES[TOTAL_TOKEN_MILESTONES.length - 1];
const nextMilestone = computed(() => TOTAL_TOKEN_MILESTONES.find((item) => props.totalTokens < item.value) ?? finalMilestone);
const activeGoal = computed(() => {
  if (isTotalMode.value) return nextMilestone.value?.value ?? YEARLY_TOKEN_GOAL;
  if (isYearMode.value) return YEARLY_TOKEN_GOAL;
  return DAILY_TOKEN_GOAL;
});
const pct = computed(() => (props.totalTokens / activeGoal.value) * 100);
const dateLabel = computed(() => {
  if (isTotalMode.value) return "已上传总量";
  if (isYearMode.value) return `${props.date.slice(0, 4)} 年`;
  return props.isToday ? "今日" : props.date;
});
const currentLabel = computed(() => (props.totalTokens ? compact.format(props.totalTokens) : "0"));
const goalLabel = computed(() => {
  if (isTotalMode.value) return `/ ${nextMilestone.value?.label ?? "1000 亿"} token`;
  if (isYearMode.value) return "/ 1000 亿 token";
  return "/ 1 亿 token";
});
const targetYear = computed(() => props.date.slice(0, 4));
const yearLabel = computed(() => `${targetYear.value} 年我的目标`);
const yearCurrentLabel = computed(() => (props.yearTotal ? compact.format(props.yearTotal) : "0"));
const verdict = computed(() => {
  if (isTotalMode.value) {
    if (props.totalTokens === 0) return "还没有已上传记录";
    const next = nextMilestone.value;
    if (!next || props.totalTokens >= next.value) return "1000 亿长期目标已达成";
    return `下一站 ${next.label}，还差 ${compact.format(next.value - props.totalTokens)}`;
  }
  if (isYearMode.value) {
    if (props.totalTokens === 0) return "今年还没有上传";
    if (pct.value >= 100) return "年度目标已达成";
    return `还差 ${compact.format(YEARLY_TOKEN_GOAL - props.totalTokens)}`;
  }
  if (props.totalTokens === 0) return props.isToday ? "今天还没有上传" : "这天没有上传";
  if (pct.value >= 100) return props.isToday ? "今日已达成，继续烧" : "这天已达成";
  return `还差 ${compact.format(DAILY_TOKEN_GOAL - props.totalTokens)}`;
});
const yearPct = computed(() => (props.yearTotal == null ? 0 : (props.yearTotal / YEARLY_TOKEN_GOAL) * 100));
</script>

<template>
  <section class="goal-section">
    <div class="container goal-board">
      <div class="goal-top">
        <div>
          <div class="goal-label">{{ dateLabel }} · 我的目标</div>
          <div class="goal-current">
            <span class="goal-number">{{ currentLabel }}</span>
            <span class="goal-of">{{ goalLabel }}</span>
          </div>
        </div>
        <div class="goal-right">
          <span class="goal-pct">{{ Math.round(pct) }}%</span>
          <div class="goal-verdict">{{ verdict }}</div>
        </div>
      </div>
      <div class="goal-bar">
        <div class="goal-bar-fill" :style="{ width: `${Math.min(100, pct)}%` }"></div>
      </div>
      <div v-if="isTotalMode" class="goal-milestones">
        <div
          v-for="milestone in TOTAL_TOKEN_MILESTONES"
          :key="milestone.value"
          class="goal-milestone"
          :class="{ reached: totalTokens >= milestone.value }"
        >
          <span>{{ milestone.label }}</span>
          <strong>{{ milestone.caption }}</strong>
        </div>
      </div>
      <template v-else-if="yearTotal != null">
        <div class="goal-year-row">
          <span class="goal-year-label">{{ yearLabel }}</span>
          <span class="goal-year-value">{{ yearCurrentLabel }} / 1000 亿 token · {{ yearPct.toFixed(1) }}%</span>
        </div>
        <div class="goal-year-bar">
          <div class="goal-year-fill" :style="{ width: `${Math.min(100, yearPct)}%` }"></div>
        </div>
      </template>
    </div>
  </section>
</template>
